/**
 * LedLevelMeter — iter-254
 *
 * A vertical hardware-style LED column that visualises real-time audio
 * level. Designed to drop into the Suno "Songs ready" cards next to
 * each Play button. The look is intentionally retro-mixer:
 *
 *   • 10-segment ladder (configurable via `segments` prop)
 *   • Bottom 6 LEDs glow green, next 2 amber, top 2 red ("clip")
 *   • Active LEDs get full saturation + box-shadow glow; inactive ones
 *     are dim (opacity 0.12) with no shadow
 *   • Peak-hold indicator — the brightest segment lingers ~700 ms
 *     after the live level drops, mimicking analog VU peak hold
 *   • Idle ("not playing") state: a slow breathing pulse on the
 *     bottom-most LED so the meter looks armed instead of dead
 *
 * Source of the live level is an AnalyserNode passed in via
 * `analyserRef`. The meter owns its own RAF loop; it samples the
 * analyser's frequency data, computes an RMS-ish level, smooths
 * upward fast / downward slow, and writes the result directly to
 * each LED's `data-on` attribute. We bypass React state on every
 * frame — touching 10 DOM nodes via refs is ~30× cheaper than a
 * setState rerender at 60 fps.
 *
 * Visual side-effect only — does NOT affect the audio graph.
 */
import { useEffect, useRef } from "react";

const DEFAULT_SEGMENTS = 10;
// Color zones: indices 0..5 → green, 6..7 → amber, 8..9 → red.
// (For a 10-segment meter; we scale these thresholds for other sizes.)
const GREEN_HEX = "#22c55e"; // tailwind green-500
const AMBER_HEX = "#f59e0b"; // tailwind amber-500
const RED_HEX = "#ef4444";   // tailwind red-500

function colorForIndex(idx, total) {
  const pct = idx / (total - 1); // 0 at bottom, 1 at top
  if (pct >= 0.8) return RED_HEX;
  if (pct >= 0.6) return AMBER_HEX;
  return GREEN_HEX;
}

export default function LedLevelMeter({
  analyserRef,            // React ref → AnalyserNode | null
  peak,                   // OR a direct 0..1 level (e.g. engine-side RMS). Takes precedence over analyserRef.
  playing = false,        // when false the meter shows a dim breathing idle state
  segments = DEFAULT_SEGMENTS,
  orientation = "vertical",
  className = "",
  ariaLabel = "Audio level meter",
  testid = "led-level-meter",
}) {
  const containerRef = useRef(null);
  const ledRefs = useRef([]);
  const peakRef = useRef(0);        // 0..1 — latched peak, decays slowly
  const peakHoldUntilRef = useRef(0); // wall-clock ms after which peak starts to decay
  const smoothedRef = useRef(0);    // smoothed live level
  // iter-255 — write the incoming `peak` prop to a ref on every render so
  // the RAF tick can read it without React having to wire a new closure.
  // When `peak == null`, the analyser path runs as the level source.
  const peakInputRef = useRef(null);
  peakInputRef.current = typeof peak === "number" ? peak : null;

  useEffect(() => {
    let raf = 0;
    let scratch = new Uint8Array(64); // re-sized when the analyser shows up
    let lastClippingState = "0";

    const tick = (ts) => {
      raf = requestAnimationFrame(tick);
      const analyser = analyserRef?.current || null;
      const directPeak = peakInputRef.current;
      let level = 0;
      if (playing && typeof directPeak === "number") {
        // iter-255 — Direct level source path (mixer channel strips).
        // The engine has already computed a smoothed RMS-ish peak per
        // channel, so we trust it as-is and just feed it through the
        // same attack/release smoothing + peak-hold so the visual
        // motion stays consistent with the analyser path.
        level = Math.max(0, Math.min(1, directPeak));
      } else if (playing && analyser) {
        // Resize the scratch buffer if the analyser's fftSize changed
        // since we last looked (e.g. first time we see this analyser).
        const need = analyser.frequencyBinCount;
        if (scratch.length !== need) scratch = new Uint8Array(need);
        analyser.getByteFrequencyData(scratch);
        // RMS of the magnitude spectrum gives a perceptually-reasonable
        // single number for VU display. Lower bins (bass) dominate
        // typical music, so we DON'T bias toward higher bins — the
        // signal already looks musical without weighting.
        let sumSq = 0;
        for (let i = 0; i < scratch.length; i++) sumSq += scratch[i] * scratch[i];
        const rms = Math.sqrt(sumSq / scratch.length); // 0..255-ish
        level = Math.min(1, rms / 140); // 140 chosen empirically — sits ~ -6 dBFS
      } else if (playing) {
        // Playing but analyser still warming up — show a low-level pulse
        // so the user knows the meter is alive.
        level = 0.08 + 0.04 * Math.sin(ts * 0.005);
      } else {
        // Idle — slow breathing on the bottom-most green LED only.
        level = 0.05 + 0.05 * Math.sin(ts * 0.002);
      }

      // Asymmetric smoothing — fast attack (0.7) so transients pop,
      // slow release (0.12) so the meter doesn't strobe.
      const prev = smoothedRef.current;
      const alpha = level > prev ? 0.7 : 0.12;
      const smoothed = prev + (level - prev) * alpha;
      smoothedRef.current = smoothed;

      // Peak hold — latch peaks for 700 ms, then decay at ~1 unit/sec.
      if (smoothed > peakRef.current) {
        peakRef.current = smoothed;
        peakHoldUntilRef.current = ts + 700;
      } else if (ts > peakHoldUntilRef.current) {
        peakRef.current = Math.max(smoothed, peakRef.current - 0.012);
      }

      // Paint — touch each LED's data attribute. The CSS rule for
      // `[data-on='1']` lights it up; `[data-on='0']` dims it.
      const litIndex = Math.floor(smoothed * segments);
      const peakIndex = Math.floor(peakRef.current * segments);
      // iter-255 — Suppress the peak-indicator segment when both the
      // smoothed level and the latched peak are essentially zero —
      // otherwise segment 0 would always show as "peak", which lies
      // about there being signal on a silent channel.
      const showPeak = peakRef.current > 0.02 || smoothed > 0.02;
      for (let i = 0; i < segments; i++) {
        const node = ledRefs.current[i];
        if (!node) continue;
        const isLit = i < litIndex;
        const isPeak = showPeak && i === Math.min(segments - 1, peakIndex);
        const desired = (isLit || isPeak) ? "1" : "0";
        if (node.dataset.on !== desired) node.dataset.on = desired;
      }

      // iter-256 — Container-level `data-clipping` flag drives a pulsing
      // red halo via CSS, mirroring the "OVR" lamp on real mixing consoles.
      // Threshold of 0.92 catches the smoothed level peaking into the
      // top red zone of a 10/16-segment meter.
      const clippingNow = smoothed >= 0.92 || peakRef.current >= 0.95 ? "1" : "0";
      if (clippingNow !== lastClippingState) {
        const root = containerRef.current;
        if (root) root.dataset.clipping = clippingNow;
        lastClippingState = clippingNow;
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [analyserRef, playing, segments]);

  const isVertical = orientation === "vertical";
  return (
    <div
      ref={containerRef}
      data-testid={testid}
      role="meter"
      aria-label={ariaLabel}
      aria-valuemin={0}
      aria-valuemax={1}
      className={`led-meter ${isVertical ? "led-meter--vert" : "led-meter--horiz"} ${className}`}
    >
      {Array.from({ length: segments }, (_, idxRaw) => {
        // Render bottom-to-top visually: the topmost div in DOM order
        // is the LOUDEST (top of meter). So invert the index used for
        // colorForIndex so the array slot 0 (DOM-first) is the top LED.
        const idx = segments - 1 - idxRaw;
        const color = colorForIndex(idx, segments);
        return (
          <span
            key={idx}
            ref={(el) => { ledRefs.current[idx] = el; }}
            data-on="0"
            data-testid={`${testid}-seg-${idx}`}
            className="led-meter__seg"
            style={{ "--led-color": color }}
          />
        );
      })}
    </div>
  );
}
